import { inject, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useGlobalStore } from '@/store'


// Redirect to send
async function redirectToSend(parsedData) {
    try {
        const store = useGlobalStore(),
            router = useRouter()

        // Change network
        if (store.currentNetwork !== parsedData[1]) {
            // Redirect
            router.push({ path: '/account' })

            // Set new current network
            await store.setCurrentNetwork(parsedData[1])

            // Wait isInitialized
            const stopWatch = watch(computed(() => store.isInitialized), () => {
                if (store.isInitialized && parsedData[0] === 'send') {
                    // Stop watch
                    stopWatch()

                    // Redirect to send page
                    router.push({
                        path: '/account/send',
                        query: {
                            denom: store.networks[store.currentNetwork].denom,
                            address: parsedData[2],
                            amount: parsedData[3]
                        }
                    })
                }
            })
        } else {
            // Redirect to send
            if (parsedData[0] === 'send') {
                router.push({
                    path: '/account/send',
                    query: {
                        denom: store.networks[store.currentNetwork].denom,
                        address: parsedData[2],
                        amount: parsedData[3]
                    }
                })
            }
        }
    } catch (error) {
        throw new Error(`Error redirecting to sending page: ${error.message}`)
    }
}


// Validate Telegram WebApp environment
function validateTelegramEnvironment() {
    // Check if we're in Telegram WebApp environment
    if (!window.Telegram || !window.Telegram.WebApp) {
        throw new Error('Not running in Telegram WebApp environment')
    }
    
    // Validate WebApp object structure
    const requiredMethods = ['ready', 'expand', 'setHeaderColor', 'onEvent']
    const missingMethods = requiredMethods.filter(method => 
        typeof window.Telegram.WebApp[method] !== 'function'
    )
    
    if (missingMethods.length > 0) {
        throw new Error(`Missing required Telegram WebApp methods: ${missingMethods.join(', ')}`)
    }
    
    return true
}

// Safe data parsing for QR codes
function parseQRData(data) {
    if (!data || typeof data !== 'string') {
        throw new Error('Invalid QR data format')
    }
    
    const parts = data.split('|')
    if (parts.length < 4) {
        throw new Error('Invalid QR data structure')
    }
    
    // Validate each part
    const [action, network, address, amount] = parts
    
    if (action !== 'send') {
        throw new Error('Unsupported QR action')
    }
    
    if (!network || typeof network !== 'string') {
        throw new Error('Invalid network in QR data')
    }
    
    if (!address || typeof address !== 'string') {
        throw new Error('Invalid address in QR data')
    }
    
    if (!amount || isNaN(parseFloat(amount))) {
        throw new Error('Invalid amount in QR data')
    }
    
    return { action, network, address, amount }
}

// Telegram WebApp init
export const tgInit = async () => {
    try {
        const store = useGlobalStore(),
            i18n = inject('i18n')

        // Validate Telegram environment
        validateTelegramEnvironment()

        // Initialize the mini-application
        await window.Telegram.WebApp.ready()

        // Call the expand method to open to full height
        window.Telegram.WebApp.expand()

        // Set header color
        window.Telegram.WebApp.setHeaderColor('#173061')

        // Show progress
        if (window.Telegram.WebApp.MainButton) {
            window.Telegram.WebApp.MainButton.showProgress(true)
        }

        // Disable vertical swipes
        window.Telegram.WebApp.disableVerticalSwipes()

        // Init biometric (with error handling)
        try {
            if (window.Telegram.WebApp.BiometricManager) {
                window.Telegram.WebApp.BiometricManager.init()
            }
        } catch (biometricError) {
            console.warn('Biometric initialization failed:', biometricError.message)
        }

        // Age confirmation is automatically approved
        await store.setAgeConfirmed()

        // Qr code received (with validation)
        window.Telegram.WebApp.onEvent('qrTextReceived', async data => {
            try {
                // Close QR popup
                window.Telegram.WebApp.closeScanQrPopup()

                // Parse and validate data
                const parsedData = parseQRData(data.data)

                // Redirect to send
                await redirectToSend([parsedData.action, parsedData.network, parsedData.address, parsedData.amount])
            } catch (error) {
                console.error('Error processing QR code:', error.message)
                // Show error notification to user
                // You might want to add a notification system here
            }
        })
    } catch (error) {
        throw new Error(`Error TG initializating: ${error.message}`)
    }
}


export default tgInit